import React, { useContext, useState } from 'react';
import { Box, Typography, FormControlLabel } from '@mui/material';
import BulletList from './bullet-list';
import NumberList from './number-list';
import Model3DViewer from './model3DViewer';
import Callout from './callout';
import StoreContext from '../../../react-core-lib/components/app/store-context';
import HandoutImageManager from './handout-image-manager';
import HandoutImageLoader from './handout-image-loader';
import IconStrip from './icon-strip';
import Copyright from './copyright';
import { GlobalStyles } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import { toJS } from 'mobx';
import AntSwitch from '../../UIControls/AntSwitch';
import DisclaimerNotice from './disclaimer-notice';

function DisplayHandout({
    handoutData,
    clinicData,
    show3DModel = true,
    showMainIllustration = true,
    showTutorialVideo = true,
    clinicName = '',
}) {
    console.warn('displayHandout handoutData', toJS(handoutData));

    const { imageStore } = useContext(StoreContext);
    const theme = useTheme();

    const locale = 'en-UK';
    const localeData = handoutData.contentData[locale];
    const [showLabels, setShowLabels] = useState(true);

    const isSharedWhiteboard = !!handoutData.whiteboardId;
    const vetName = isSharedWhiteboard
        ? `${handoutData.updatedByFirstName || ''} ${handoutData.updatedByLastName || ''}`.trim()
        : '';

    if (!localeData) {
        throw new Error(`No data found for the specified locale: ${locale}`);
    }

    return (
        <HandoutImageManager handoutData={handoutData}>
            <GlobalStyles
                styles={{
                    '@media print': {
                        // The content should have a bottom padding equal to or larger than the footer height
                        // to prevent content from overlapping with the footer
                        '#handout-content': {
                            paddingBottom: '100px', // Adjust based on the actual height of your footer
                        },
                    },
                }}
            />
            <Box id="handout-content" sx={{ my: 0, position: 'relative' }}>
                {localeData.map((section, index) => generateSection(section, index))}
            </Box>
            {isSharedWhiteboard && (
                <Box sx={{ mb: 4 }}>
                    <DisclaimerNotice vetName={vetName} clinicName={clinicData?.clinicName || ''} />
                </Box>
            )}
            <Copyright clinicData={clinicData} />
        </HandoutImageManager>
    );

    function generateSection(jsonContent, index) {
        const sectionName = jsonContent.sectionName;
        const isPrintPageBreak = sectionName === 'printPageBreak';
        // Handle 'diagram' section specially to toggle between labeled and unlabeled images
        let blocks = sectionName === 'diagram' ? [jsonContent.blocks[showLabels ? 0 : 1]] : jsonContent.blocks;

        // added filtering to catch toggle states
        blocks = blocks.filter((block) => {
            if (block.type === '3dModel' && !show3DModel) return false;
            if (block.type === 'illustration' && !showMainIllustration) return false;
            if (block.type === 'tutorialVideo' && !showTutorialVideo) return false;
            return true;
        });
        console.log('blocks 2:', blocks);

        // If no blocks remain after filtering, skip rendering this section
        if (blocks.length === 0) {
            return null;
        }

        return (
            <React.Fragment key={index}>
                <Box
                    key={jsonContent.sectionName}
                    className={`${sectionName}${isPrintPageBreak ? ' printPageBreak' : ''}`}
                    component="section"
                    sx={{
                        my: 5,
                        '@media print': {
                            // pageBreakInside: 'avoid',
                            // pageBreakAfter: 'auto', // This will add a break after the section if needed
                            // marginBottom: '30px', // Adjust this value as needed for proper spacing
                        },
                    }}
                >
                    {/* 'diagram' section, can be confused */}
                    {sectionName === 'diagram' && blocks && blocks[0] && !isSharedWhiteboard && (
                        <FormControlLabel
                            control={<AntSwitch checked={showLabels} onChange={() => setShowLabels((prev) => !prev)} />}
                            label="Labels"
                            labelPlacement="start"
                            sx={{
                                position: 'absolute',
                                top: '16px',
                                right: 0,
                                '& .MuiFormControlLabel-label': { marginRight: '8px' },
                            }}
                        />
                    )}
                    {blocks.map((block, index) => (
                        <React.Fragment key={index}>{blockToComponentMapping(block, sectionName)}</React.Fragment>
                    ))}
                </Box>
                {isPrintPageBreak && <div></div>}
            </React.Fragment>
        );
    }

    function blockToComponentMapping(block) {
        if (!block || !block.type) {
            console.error(`Block is missing type or is undefined`, block);
            return null;
        }
        switch (block.type) {
            case 'documentTitle':
                return (
                    <Typography key={block.value} variant="documentTitle" sx={{ mb: 2 }} className={`${block.type}`}>
                        {block.value}
                    </Typography>
                );

            case 'sectionTitle':
                return (
                    <Typography key={block.value} variant="sectionTitle" sx={{ mb: 2 }} className={`${block.type}`}>
                        {block.value}
                    </Typography>
                );
            case 'sectionSubtitle':
                return (
                    <Typography key={block.value} variant="sectionSubtitle" sx={{ mb: 2 }} className={`${block.type}`}>
                        {block.value}
                    </Typography>
                );
            case 'calloutSmall':
                return (
                    <Callout key={block.value} className={`${block.type}`}>
                        {block.value}
                    </Callout>
                );
            case 'calloutMedium':
                return (
                    <Callout key={block.value} className={`${block.type}`}>
                        {block.value}
                    </Callout>
                );
            case 'paragraph':
                return (
                    <Typography key={block.value} sx={{ my: 2 }} className={`${block.type}`}>
                        {block.value}
                    </Typography>
                );
            case 'list':
                return <BulletList items={block.values} className={`${block.type}`} />;
            case 'numberList':
                return <NumberList items={block.values} />;
            case 'iconStrip':
                return (
                    <IconStrip iconDetails={block.iconDetails} imageStore={imageStore} className={`${block.type}`} />
                );
            case 'illustration':
                return (
                    <HandoutImageLoader
                        src={block.src}
                        alt={block.alt}
                        imageStore={imageStore}
                        className={`${block.type}`}
                    />
                );
            case '3dModel': // 3D Model thing!
                return (
                    <Model3DViewer uid={block.uid} alt={block.alt} author={block.author} authorWeb={block.authorWeb} />
                );
            case 'printPageBreak':
                return (
                    <>
                        <div className="printPageBreak"></div>
                        <p></p>
                    </>
                );
            default:
                return null;
        }
    }
}

export default DisplayHandout;
